import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { pedidosRecurrentesAPI, handleAPIError } from '../../services/api';

// Thunks para operaciones asíncronas
export const fetchRecurringOrders = createAsyncThunk(
  'recurringOrders/fetchRecurringOrders',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const { data } = await pedidosRecurrentesAPI.getAll(filters);
      return data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const fetchRecurringOrderById = createAsyncThunk(
  'recurringOrders/fetchRecurringOrderById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await pedidosRecurrentesAPI.getById(id);
      return data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const createRecurringOrder = createAsyncThunk(
  'recurringOrders/createRecurringOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await pedidosRecurrentesAPI.create(orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const updateRecurringOrder = createAsyncThunk(
  'recurringOrders/updateRecurringOrder',
  async ({ id, orderData }, { rejectWithValue }) => {
    try {
      const response = await pedidosRecurrentesAPI.update(id, orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const deleteRecurringOrder = createAsyncThunk(
  'recurringOrders/deleteRecurringOrder',
  async (id, { rejectWithValue }) => {
    try {
      await pedidosRecurrentesAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

const recurringOrdersSlice = createSlice({
  name: 'recurringOrders',
  initialState: {
    orders: [],
    selectedOrder: null,
    loading: false,
    error: null,
    filters: {
      search: '',
      tipo: 'all',
      temporada: null,
      cliente_id: 'all',
    },
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        tipo: 'all',
        temporada: null,
        cliente_id: 'all',
      };
    },
    setSelectedOrder: (state, action) => {
      state.selectedOrder = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all recurring orders
      .addCase(fetchRecurringOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecurringOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
      })
      .addCase(fetchRecurringOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single recurring order
      .addCase(fetchRecurringOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecurringOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrder = action.payload;
      })
      .addCase(fetchRecurringOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create recurring order
      .addCase(createRecurringOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRecurringOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.unshift(action.payload);
      })
      .addCase(createRecurringOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update recurring order
      .addCase(updateRecurringOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRecurringOrder.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.orders.findIndex((order) => order.id === action.payload.id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      .addCase(updateRecurringOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete recurring order
      .addCase(deleteRecurringOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRecurringOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = state.orders.filter((order) => order.id !== action.payload);
      })
      .addCase(deleteRecurringOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearFilters, setSelectedOrder, clearError } = recurringOrdersSlice.actions;

// Selectors
export const selectRecurringOrders = (state) => {
  const { orders, filters } = state.recurringOrders;
  
  let filtered = [...orders];
  
  // Filtrar por búsqueda
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter((order) => {
      const clienteName = order.clientes?.nombre?.toLowerCase() || '';
      const clienteCode = order.clientes?.codigo_cliente?.toLowerCase() || '';
      return clienteName.includes(searchLower) || clienteCode.includes(searchLower);
    });
  }
  
  // Filtrar por tipo
  if (filters.tipo && filters.tipo !== 'all') {
    filtered = filtered.filter((order) => order.tipo === filters.tipo);
  }
  
  // Filtrar por temporada
  if (filters.temporada) {
    filtered = filtered.filter((order) => order.temporada === filters.temporada);
  }
  
  // Filtrar por cliente
  if (filters.cliente_id && filters.cliente_id !== 'all') {
    filtered = filtered.filter((order) => order.cliente_id === parseInt(filters.cliente_id));
  }
  
  return filtered;
};

export const selectRecurringOrdersLoading = (state) => state.recurringOrders.loading;
export const selectRecurringOrdersError = (state) => state.recurringOrders.error;
export const selectRecurringOrdersFilters = (state) => state.recurringOrders.filters;
export const selectSelectedRecurringOrder = (state) => state.recurringOrders.selectedOrder;

export default recurringOrdersSlice.reducer;
